import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCreateContactSubmissionMutation, useGetPublicStatisticsQuery, useGetPublicProjectsQuery } from '../store/slices/apiSlice';
import toast from 'react-hot-toast';
import { usePageMetadata } from '../hooks/usePageMetadata';
import {
    Mail,
    MessageCircle,
    Send,
    CheckCircle,
    Users,
    Award,
    Shield,
    Facebook,
    Twitter,
    Linkedin,
    Instagram,
    Youtube,
    Sparkles,
    Building2,
    Headphones,
    Zap
} from 'lucide-react';

import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';

const Contact = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [activeTab, setActiveTab] = useState('general');

    // Set page metadata
    usePageMetadata('contact');
    
    // Fetch real statistics
    const { data: statsData } = useGetPublicStatisticsQuery();
    const { data: projectsResponse } = useGetPublicProjectsQuery();
    const projects = Array.isArray(projectsResponse?.data) ? projectsResponse?.data :
        Array.isArray(projectsResponse?.data?.projects) ? projectsResponse?.data?.projects : [];

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // API mutation for creating contact submission
    const [createContactSubmission] = useCreateContactSubmissionMutation();

    useEffect(() => {
        setIsVisible(true);
    }, []);

    // Format number helper
    const formatNumber = (num: number) => {
        if (num >= 1000000) {
            return `${(num / 1000000).toFixed(1)}M+`;
        } else if (num >= 1000) {
            return `${(num / 1000).toFixed(1)}K+`;
        }
        return num.toString();
    };

    // Calculate funded projects count
    const projectsFunded = projects.filter((project: any) => {
        const isFunded = project.is_funded === true;
        const fundingGoal = parseFloat(project.funding_goal || project.original_funding_goal || '0');
        const amountRaised = parseFloat(project.amount_raised || project.original_amount_raised || '0');
        return isFunded || (fundingGoal > 0 && amountRaised >= fundingGoal);
    }).length;

    // Estimate investors from projects if API data not available
    const estimatedInvestors = Math.max(projectsFunded * 15, projects.length * 8);

    // Get real stats
    const totalUsers = statsData?.data?.total_users || estimatedInvestors;
    const totalProjects = statsData?.data?.total_projects || projects.length;



    // FAQ Data
    const faqs = {
        general: [
            {
                question: "How do I get started with Diasporawise?",
                answer: "Getting started is easy! Simply create an account, complete your profile verification, and browse our available investment opportunities. Our team will guide you through the entire process."
            },
            {
                question: "What types of investments are available?",
                answer: "We offer a diverse range of investment opportunities including real estate, technology startups, renewable energy projects, and more. Each opportunity is carefully vetted for quality and potential returns."
            },
            {
                question: "Is my investment secure?",
                answer: "Yes, we prioritize security and transparency. All investments are backed by proper legal documentation, and we maintain strict compliance with regulatory requirements."
            }
        ],
        technical: [
            {
                question: "How do I access my investment dashboard?",
                answer: "You can access your dashboard by logging into your account on our website or mobile app. All your investments, returns, and portfolio performance are displayed in real-time."
            },
            {
                question: "What payment methods are accepted?",
                answer: "We accept various payment methods including bank transfers, UPI, and digital wallets. All transactions are secure and encrypted."
            },
            {
                question: "How do I track my investment returns?",
                answer: "Your investment returns are automatically calculated and displayed in your dashboard. You'll receive regular updates and detailed reports on your portfolio performance."
            }
        ],
        support: [
            {
                question: "How can I contact customer support?",
                answer: "You can reach our support team through multiple channels: phone, email, live chat, or by submitting a ticket through your account dashboard."
            },
            {
                question: "What are your support hours?",
                answer: "Our support team is available Monday to Friday, 9:00 AM to 6:00 PM IST. For urgent matters, you can also reach us through our emergency contact number."
            },
            {
                question: "How long does it take to get a response?",
                answer: "We typically respond to inquiries within 24 hours. For urgent matters, we aim to respond within 2-4 hours during business hours."
            }
        ]
    };

    // Social Media Links
    const socialLinks = [
        { name: 'Twitter', icon: Twitter, url: 'https://twitter.com/diasporawise', color: 'from-blue-400 to-blue-600' },
        { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/diasporawise', color: 'from-blue-500 to-blue-700' },
        { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/diasporawise', color: 'from-blue-600 to-blue-800' },
        { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/diasporawise', color: 'from-purple-400 to-pink-500' },
        { name: 'YouTube', icon: Youtube, url: 'https://youtube.com/diasporawise', color: 'from-red-500 to-red-600' }
    ];

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Prepare the data for the API
            const submissionData = {
                name: `${formData.firstName} ${formData.lastName}`.trim(),
                email: formData.email,
                subject: formData.subject,
                message: formData.message
            };

            // Call the API
            const response = await createContactSubmission(submissionData).unwrap();

            if (response.success) {
                toast.success('Message sent successfully! We\'ll get back to you within 24 hours.');
                setShowSuccess(true);

                // Reset form
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    company: '',
                    subject: '',
                    message: ''
                });

                setTimeout(() => setShowSuccess(false), 5000);
            } else {
                toast.error('Failed to send message. Please try again.');
            }
        } catch (error: any) {
            console.error('Contact submission error:', error);
            const errorMessage = error?.data?.message || 'Failed to send message. Please try again.';
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <Navbar />

            {/* Simple Hero Section */}
            <section className="relative py-12 px-6 bg-white border-b border-gray-200">
                <div className="container mx-auto max-w-7xl">
                    <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <div className="text-center">
                            <div className="inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full text-sm font-medium mb-6 text-gray-600 border border-gray-200">
                                <MessageCircle className="w-4 h-4" />
                                Get In Touch
                            </div>

                            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                                Let's Build Your Investment Future
                            </h1>

                            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                                Ready to start your investment journey? Our expert team is here to help you navigate
                                the world of opportunities and build a prosperous future.
                            </p>
                        </div>
                    </div>
                </div>
            </section>



            {/* Contact Form & Office Locations */}
            <section className="py-16 px-6 bg-white">
                <div className="container mx-auto max-w-6xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Contact Form */}
                        <div className="bg-gray-50 rounded-2xl p-8">
                            <div className="mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    Send Us a Message
                                </h2>
                                <p className="text-gray-600 text-sm">
                                    We'll get back to you within 24 hours.
                                </p>
                            </div>

                            {showSuccess && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                        <div>
                                            <h3 className="font-semibold text-green-900 text-sm">Message Sent!</h3>
                                            <p className="text-green-700 text-xs">We'll get back to you soon.</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                                            placeholder="First Name *"
                                        />
                                    </div>
                                    <div>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                                            placeholder="Last Name *"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                                        placeholder="Email Address *"
                                    />
                                </div>


                                <div>
                                    <select
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                                    >
                                        <option value="">Select Subject *</option>
                                        <option value="general">General Inquiry</option>
                                        <option value="investment">Investment Opportunities</option>
                                        <option value="support">Technical Support</option>
                                        <option value="partnership">Partnership</option>
                                        <option value="feedback">Feedback</option>
                                    </select>
                                </div>

                                <div>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        required
                                        rows={4}
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 resize-none"
                                        placeholder="Your Message *"
                                    />
                                </div>


                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4" />
                                            Send Message
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>

                        {/* Contact Info */}
                        <div>
                            <div className="mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    Contact Information
                                </h2>
                                <p className="text-gray-600 text-sm">
                                    Get in touch with us for any inquiries.
                                </p>
                            </div>

                            <div className="space-y-4">
                                {/* Email */}
                                <div className="bg-white rounded-lg p-6 border border-gray-200">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                                            <Mail className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900">Email</h3>
                                            <a
                                                href="mailto:info@diasporawise.com"
                                                className="text-blue-600 hover:text-blue-700 text-sm transition-colors duration-300"
                                            >
                                                info@diasporawise.com
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 px-6 bg-gray-50">
                <div className="container mx-auto max-w-4xl">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-900 mb-6">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-xl text-gray-600">
                            Find answers to common questions about our investment platform.
                        </p>
                    </div>

                    {/* FAQ Tabs */}
                    <div className="flex flex-wrap justify-center gap-4 mb-12">
                        {[
                            { id: 'general', name: 'General', icon: Users },
                            { id: 'technical', name: 'Technical', icon: Zap },
                            { id: 'support', name: 'Support', icon: Headphones }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${activeTab === tab.id
                                    ? 'bg-blue-600 text-white shadow-lg'
                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                    }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.name}
                            </button>
                        ))}
                    </div>

                    {/* FAQ Content */}
                    <div className="space-y-4">
                        {faqs[activeTab as keyof typeof faqs].map((faq, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                                    {faq.question}
                                </h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {faq.answer}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Social Media & CTA */}
            <section className="py-20 px-6 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600">
                <div className="container mx-auto max-w-4xl text-center">
                    <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-semibold text-white mb-6">
                        <Sparkles className="w-4 h-4" />
                        Connect With Us
                    </div>

                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Stay Updated
                    </h2>

                    <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                        Follow us on social media for the latest investment insights, market updates, and exclusive opportunities.
                    </p>

                    {/* Social Media Links */}
                    <div className="flex flex-wrap justify-center gap-6 mb-12">
                        {socialLinks.map((social, index) => (
                            <a
                                key={index}
                                href={social.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group"
                            >
                                <div className={`w-16 h-16 bg-gradient-to-br ${social.color} rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group-hover:scale-110`}>
                                    <social.icon className="w-6 h-6 text-white" />
                                </div>
                                <p className="text-xs text-white/80 mt-2">{social.name}</p>
                            </a>
                        ))}
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/projects"
                            className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors duration-300 transform hover:-translate-y-1 hover:scale-105"
                        >
                            Explore Investments
                        </Link>
                        <Link
                            to="/blogs"
                            className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl font-semibold hover:bg-white/20 transition-colors duration-300"
                        >
                            Read Our Blog
                        </Link>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <FAQ
                title="Contact & Support Questions"
                subtitle="Get answers to common questions about contacting us and getting support"
                faqs={[
                    {
                        id: 1,
                        question: "What are your customer support hours?",
                        answer: "Our customer support team is available Monday through Friday from 9:00 AM to 6:00 PM EST. For urgent matters, we also provide 24/7 emergency support for our premium investors. You can reach us via phone, email, or through our live chat feature on the website."
                    },
                    {
                        id: 2,
                        question: "How can I get in touch with your team?",
                        answer: "You can contact us through multiple channels: email at info@diasporawise.com, or by filling out the contact form on this page. We also have live chat available on our website and maintain active social media presence for general inquiries."
                    },
                    {
                        id: 3,
                        question: "What information should I include in my inquiry?",
                        answer: "To help us assist you better, please include your full name, email address, account number (if applicable), and a detailed description of your question or concern. For investment-related inquiries, please specify the project name or investment ID if relevant."
                    },
                    {
                        id: 4,
                        question: "How quickly can I expect a response?",
                        answer: "We typically respond to general inquiries within 24 hours during business days. For urgent matters or existing investors, we aim to respond within 4-6 hours. Investment-related questions may take up to 48 hours as they often require consultation with our expert team."
                    },
                    {
                        id: 5,
                        question: "Do you offer phone consultations for investment advice?",
                        answer: "Yes, we offer phone consultations for our investors and potential investors. These consultations are conducted by our investment specialists and can be scheduled through our support team. Please note that phone consultations are available for investments above certain thresholds and may require prior appointment scheduling."
                    },
                    {
                        id: 6,
                        question: "Can I visit your office in person?",
                        answer: "While we primarily operate as a digital platform, we do have physical offices in major financial centers. In-person meetings can be arranged by appointment for significant investment discussions or business partnerships. Please contact us in advance to schedule an appointment and confirm availability."
                    }
                ]}
            />

            <Footer />
        </div>
    );
};

export default Contact; 